import React from 'react';
import { Award, Users, TrendingUp, Target } from 'lucide-react';

const AboutSection = () => {
  const highlights = [
    {
      icon: <Award className="w-8 h-8" />,
      number: '16+',
      label: 'Years Experience',
    },
    {
      icon: <Users className="w-8 h-8" />,
      number: '50+',
      label: 'Projects Delivered',
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      number: '30%',
      label: 'Avg Velocity Increase',
    },
    {
      icon: <Target className="w-8 h-8" />,
      number: '$5M+',
      label: 'Projects Managed',
    },
  ];

  return (
    <section id="about" className="py-20 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="about-title">
            About Me
          </h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto"></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Text Content */}
          <div className="space-y-6">
            <p className="text-lg text-gray-700 leading-relaxed" data-testid="about-intro">
              I am a <span className="font-semibold text-blue-600">Strategic Project Manager</span> with over 16 years of experience 
              driving enterprise product vision, roadmap strategy, and digital transformation across Banking, Insurance, Technology, Healthcare, Food Service, and Telecom sectors.
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed">
              My expertise lies in <span className="font-semibold text-blue-600">Agile/SAFe program leadership</span>, cloud modernization 
              (Azure, Salesforce, microservices), and managing complex Adobe Experience Manager (AEM) projects. I oversee the full content 
              lifecycle from authoring to publication.
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed">
              I specialize in <span className="font-semibold text-blue-600">risk management and capacity planning</span> for global 
              cross-functional teams, bringing a strong technical foundation in Adobe Creative Suite (Target, Campaign, Marketo) and 
              cloud modernization with Azure.
            </p>

            <div className="pt-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Core Competencies</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Agile & SAFe Methodologies</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Digital Transformation</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Cloud Modernization</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Risk Management</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Team Leadership</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Stakeholder Management</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Stats Cards */}
          <div className="grid grid-cols-2 gap-6">
            {highlights.map((item, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 text-center hover:shadow-lg transition-shadow"
                data-testid={`stat-card-${index}`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 text-white rounded-full mb-4">
                  {item.icon}
                </div>
                <div className="text-3xl font-bold text-blue-900 mb-2">{item.number}</div>
                <div className="text-sm text-gray-700 font-medium">{item.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Education */}
        <div className="mt-16 bg-gray-50 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Education</h3>
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
              MBA
            </div>
            <div>
              <h4 className="text-xl font-semibold text-gray-900">Master of Business Administration</h4>
              <p className="text-gray-700">Human Resource Management</p>
              <p className="text-gray-600 mt-1">IGNOU (Indira Gandhi National Open University), New Delhi, India</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;